/**
 * emailHygieneRecoveryState — EMAIL-HYGIENE-RECOVERY-STATE-001 (Phase 1).
 *
 * The single operator-facing vocabulary for the backend-derived
 * `recovery_state` (app/services/email_hygiene_recovery_state.py). Presentation
 * only: this module contains NO eligibility logic and never derives a state of
 * its own. The backend remains the sole authority on both what happens next
 * (`recovery_state`) and what an operator may do (`available_actions`), exactly
 * as EMAIL-HYGIENE-RECON-002 requires — the frontend must never contain logic
 * equivalent to `if reason === "..." then show button`.
 *
 * `recovery_state` is Native-only. A Production Sync (propagation) row carries
 * `null`, and callers must render nothing rather than substituting a Native
 * label, because propagation records have their own separate
 * `aggregate_status`/`needs_attention` vocabulary.
 *
 * Why the raw `retry_pending` lifecycle is deliberately NOT the operator label:
 * it records only whether the provider error was transient, not whether any
 * component will act. `projection_failed`/`retry_pending` has no consumer, so
 * showing "retry pending" for it tells an operator the exact opposite of the
 * truth. The raw lifecycle remains visible in the technical CRM Projection
 * evidence section, unchanged, for audit.
 */

export const RECOVERY_STATE_PRESENTATION = {
  automatic_recovery_scheduled: {
    label: "Automatic Retry Scheduled",
    className: "bg-sky-500/20 text-sky-300",
    explanation:
      "MissionAssist will retry this automatically. No operator action is required.",
  },
  automatic_recovery_deferred: {
    label: "Waiting — Provider Backoff",
    className: "bg-sky-500/20 text-sky-300",
    explanation:
      "MissionAssist is waiting for the provider backoff period before the next automatic attempt.",
  },
  automatic_recovery_exhausted: {
    label: "Automatic Retry Exhausted",
    className: "bg-orange-500/20 text-orange-300",
    explanation:
      "Automatic retry attempts have been exhausted. No further automatic attempt is scheduled.",
  },
  provider_outcome_unverified: {
    label: "Provider Outcome Unverified",
    className: "bg-amber-500/20 text-amber-300",
    explanation:
      "A CRM update was attempted, but MissionAssist could not confirm the result. It will not resend the update blindly.",
  },
  local_commit_incomplete: {
    label: "Local State Out of Sync",
    className: "bg-amber-500/20 text-amber-300",
    explanation:
      "The CRM update was recorded as successful, but MissionAssist did not finish its local reconciliation.",
  },
  operator_action_available: {
    label: "Action Available",
    className: "bg-emerald-500/20 text-emerald-300",
    explanation: null,
  },
  operator_review_required: {
    label: "Manual Review Required",
    className: "bg-slate-500/20 text-slate-300",
    explanation: "No automatic recovery is scheduled for this reconciliation.",
  },
  // EMAIL-HYGIENE-PROVIDER-VERIFY-001 (Phase 2a): post-verification states.
  // A verification is an OBSERVATION, not a resolution -- every explanation
  // below states explicitly that the reconciliation remains open, because the
  // single most likely operator misreading is "verified" meaning "done".
  provider_evidence_present: {
    label: "Provider Evidence Present",
    className: "bg-emerald-500/20 text-emerald-300",
    explanation:
      "The CRM currently contains the expected delivery evidence. The reconciliation remains open pending governed local resolution.",
  },
  provider_evidence_superseded: {
    label: "Superseded by Newer CRM Evidence",
    className: "bg-sky-500/20 text-sky-300",
    explanation:
      "The CRM contains newer authoritative delivery evidence. MissionAssist will not overwrite it. The reconciliation remains open pending governed local resolution.",
  },
  provider_evidence_absent: {
    label: "Provider Evidence Absent",
    className: "bg-amber-500/20 text-amber-300",
    explanation:
      "The CRM does not currently contain this event's expected evidence. No provider update was sent.",
  },
  provider_verification_unavailable: {
    label: "Provider Verification Unavailable",
    className: "bg-orange-500/20 text-orange-300",
    explanation:
      "MissionAssist could not verify the current CRM state. No provider update was sent.",
  },
  resolved: {
    label: "Resolved",
    className: "bg-slate-500/20 text-slate-400",
    explanation: null,
  },
};

/** Ordered for the queue filter control. Values must match the backend enum. */
export const RECOVERY_STATE_FILTER_OPTIONS = [
  "automatic_recovery_scheduled",
  "automatic_recovery_deferred",
  "automatic_recovery_exhausted",
  "provider_outcome_unverified",
  "local_commit_incomplete",
  "operator_action_available",
  "operator_review_required",
  "provider_evidence_present",
  "provider_evidence_superseded",
  "provider_evidence_absent",
  "provider_verification_unavailable",
];

/**
 * EMAIL-HYGIENE-PROVIDER-VERIFY-001 (Phase 2a): structured explanations for
 * the three verification findings that need operator judgement. They share
 * `operator_review_required` as their recovery state -- the operator action is
 * identical for all three -- so the specific finding is carried separately
 * rather than as three more top-level statuses.
 */
export const RECOVERY_REASON_DETAIL_EXPLANATION = {
  provider_state_conflicted:
    "The CRM state could not be compared against this event's evidence. No provider update was sent.",
  provider_contact_missing_now:
    "No CRM contact currently matches this recipient. No provider update was sent.",
  provider_identity_drift:
    "The CRM contact for this recipient has changed since the original attempt. No provider update was sent.",
  // AUTO-RECONCILIATION-RECOVERY-SEMANTICS-001: the two identity-BASELINE
  // findings. Both are decided from stored evidence before the CRM is read at
  // all, so neither describes current CRM state -- the wording deliberately
  // says what could not be established, not what the CRM holds.
  historical_identity_unavailable:
    "This event's original CRM contact could not be established from its own recorded evidence, "
    + "so there is nothing to compare current CRM state against. The CRM was not read and no provider update was sent.",
  identity_evidence_inconsistent:
    "Two records of this event's original CRM contact disagree with each other, so the original identity "
    + "cannot be trusted. The CRM was not read and no provider update was sent.",
};

export function recoveryReasonDetailExplanation(detail) {
  if (!detail) return null;
  return RECOVERY_REASON_DETAIL_EXPLANATION[detail] || null;
}

/**
 * Returns the presentation entry for a backend `recovery_state`, or `null` when
 * there is none to show (a propagation row, or a value this build does not yet
 * know). Never invents a label for an unrecognized value — an unknown state is
 * rendered as absent rather than mislabelled.
 */
export function recoveryStatePresentation(recoveryState) {
  if (!recoveryState) return null;
  return RECOVERY_STATE_PRESENTATION[recoveryState] || null;
}

export function recoveryStateLabel(recoveryState) {
  return recoveryStatePresentation(recoveryState)?.label || null;
}

export function recoveryStateExplanation(recoveryState) {
  return recoveryStatePresentation(recoveryState)?.explanation || null;
}
